import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { Button, Container, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  myBrandsDocument,
  createBrandDocument,
} from '@social-garden/api/documents/brand.ts';
import {
  CreateBrandFieldValues,
  CreateBrandSchema,
} from '@/constants/ValidationSchema.ts';
import ImageCropField from '@/components/ImageCropField.tsx';
import { FormFieldProps } from '@/utils/types.ts';
import useBrandUsage from '@/hooks/useBrandUsage.ts';
import PageTitle from '@/components/PageTitle.tsx';
import BrandNameField from '@/components/BrandNameField.tsx';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';

export const Route = createFileRoute('/_manager/_app/brands/create')({
  component: CreateBrand,
});

function CreateBrand() {
  const { t } = useTranslation(['common', 'manager']);
  const navigate = useNavigate();
  const { exceeded } = useBrandUsage();
  const [createBrand, { loading }] = useMutation(createBrandDocument, {
    refetchQueries: [myBrandsDocument],
  });

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isValid, isSubmitting: isFormSubmitting },
  } = useForm<CreateBrandFieldValues>({
    mode: 'all',
    resolver: zodResolver(CreateBrandSchema),
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting: isFormSubmitting });

  const renderLogoField = useCallback(
    ({
      field: { name, onChange },
      fieldState: { error, invalid },
      formState: { isSubmitting },
    }: FormFieldProps<CreateBrandFieldValues, 'logo'>) => (
      <ImageCropField
        fullWidth
        label={t('common:brand.logo')}
        width={500}
        height={500}
        disabled={isSubmitting}
        error={invalid}
        helperText={error?.message}
        name={name}
        cropShape="round"
        onChange={onChange}
      />
    ),
    [t],
  );

  const handleOnValid = useCallback(
    async ({ logo, name }: CreateBrandFieldValues) => {
      const { data } = await createBrand({
        variables: {
          input: {
            logo,
            name,
          },
        },
      });
      if (data?.createBrand) {
        await navigate({ to: '/brands' });
      }
    },
    [createBrand, navigate],
  );

  if (exceeded) {
    navigate({ to: '/brands' });
  }

  return (
    <Container
      component="form"
      maxWidth="md"
      noValidate
      onSubmit={handleSubmit(handleOnValid)}>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('manager:brand.create.title')}</PageTitle>
        </Grid>
        <Grid container size={12} spacing={2}>
          <Grid size={12}>
            <Controller<CreateBrandFieldValues, 'logo'>
              control={control}
              render={renderLogoField}
              name="logo"
            />
          </Grid>
          <Grid size={12}>
            <Controller<CreateBrandFieldValues, 'name'>
              control={control}
              render={BrandNameField}
              name="name"
            />
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              loading={loading}
              disabled={!isValid || exceeded}>
              {t('manager:brand.create.submitButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
